import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CommandView from './CommandView';
import useTelemetry, { TelemetryControls, ActivityStrip } from './useTelemetry';
import { trafficPanel, anomalyPanel, AnomalyTable, FlowTable, RuntimeRows } from './telemetryHud';
import { deriveFleet, fleetInspector } from '../three/fleetState';
import { ago } from '../three/ecosystemState';
import { apiGet, isBackendConfigured } from '../../utils/backendApi';
import { useTenant } from '../../data/entities';
import { ORG_ASPECT, orgLive } from '../../pages/live/routes';

// Organization command view: every app around the org, cost arc against the
// org's monthly limit, usage counters. Realtime inputs (appInstance docs,
// orgs/{org}/costs/{m}, orgs/{org}/usage/{m}) — no polling, no backend route.
const usd = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`);
const n = (v) => (v == null ? '—' : Number(v).toLocaleString());
const Row = ({ k, v }) => <span className="cmd-row"><span>{k}</span><span>{v}</span></span>;

// Provisioning stages for apps mid-build. `provisionJobs` is server-only,
// so poll the per-app status route while anything is provisioning /
// claimed; the stage ring + card read `app.provisioning` from this.
function useProvisioningJobs(orgId, apps) {
  const building = useMemo(() => (apps || []).filter((a) => (a.status === 'provisioning' || a.status === 'claimed') && a.provisionJobId).map((a) => a.id), [apps]);
  const key = building.join(',');
  const [jobs, setJobs] = useState({});
  useEffect(() => {
    if (!key || !isBackendConfigured()) { setJobs({}); return undefined; }
    let stop = false;
    const tick = async () => {
      const out = {};
      await Promise.all(key.split(',').map(async (id) => {
        try { const r = await apiGet(`/apps/${orgId}/${id}/status`); if (r?.job) out[id] = r.job; } catch { /* keep the last known stages */ }
      }));
      if (!stop) setJobs((cur) => ({ ...cur, ...out }));
    };
    tick();
    const h = setInterval(tick, 15000);
    return () => { stop = true; clearInterval(h); };
  }, [orgId, key]);
  return jobs;
}

export default function OrgCommandView({ org, apps, orgCost, usage, isAdmin, chrome = 150, children }) {
  const navigate = useNavigate();
  const jobs = useProvisioningJobs(org.id, apps);
  const appsWithJobs = useMemo(() => (apps ? apps.map((a) => (jobs[a.id] ? { ...a, provisioning: jobs[a.id] } : a)) : apps), [apps, jobs]);
  const data = useMemo(() => (appsWithJobs ? { mode: 'org', org, apps: appsWithJobs, orgCost: orgCost || null, usage: usage || null } : null), [org, appsWithJobs, orgCost, usage]);
  const s = useMemo(() => deriveFleet(data), [data]);
  const c = s.center;
  const t = s.totals;
  const period = new Date().toISOString().slice(0, 7);
  const byApp = Object.entries(orgCost?.byApp || {}).sort((a, b) => b[1].mtdUsd - a[1].mtdUsd);

  const roster = useMemo(() => s.nodes.map((x) => ({ slug: x.slug, pool: x.pool, external: x.external, envRole: x.envRole, twinSlug: (apps || []).some((a) => a.slug === `${x.slug}-uat`) ? `${x.slug}-uat` : null })), [s.nodes, apps]);
  // Scoped to the sidebar's selected workspace (TenantContext), like the roster.
  const tenant = useTenant();
  const fetchReal = useCallback((w) => (isBackendConfigured() ? apiGet(`/apps/${org.id}/telemetry?window=${w}${tenant.workspace ? `&workspaceId=${encodeURIComponent(tenant.workspace)}` : ''}`) : Promise.resolve(null)), [org.id, tenant.workspace]);
  const { telemetry, controls } = useTelemetry({ surface: 'org', roster, fetchReal, enabled: !!data });
  const inspectorFor = useCallback((sel) => fleetInspector(s, telemetry, sel), [s, telemetry]);

  const liveApps = (apps || []).filter((a) => a.status === 'live' && a.envRole !== 'uat');
  const scanHigh = liveApps.filter((a) => (a.scan?.counts?.critical || 0) > 0 || (a.scan?.counts?.high || 0) > 0).length;
  const scanAccepted = liveApps.reduce((n0, a) => n0 + (a.scan?.counts?.accepted || 0), 0);
  const panels = useMemo(() => (!data ? [] : [
    {
      id: 'apps', side: 'left', level: t.down ? 'down' : s.legend.some((l) => l.key === 'warn' && l.count) ? 'warn' : t.provisioning ? 'busy' : 'ok', title: 'Apps',
      body: (
        <>
          <strong>{t.live}<span className="cmd-sub"> live · {t.instances} total</span></strong>
          {t.provisioning ? <Row k="provisioning" v={t.provisioning} /> : null}
          {t.down ? <Row k="down" v={t.down} /> : null}
          {s.legend.find((l) => l.key === 'warn')?.count ? <Row k="degraded" v={s.legend.find((l) => l.key === 'warn').count} /> : null}
          {s.nodes.filter((x) => x.status === 'live').slice(0, 4).map((x) => <Row key={x.id} k={x.name} v={x.tone === 'ok' ? (x.monitorLevel === 'off' ? 'live' : 'up') : x.issues[0]?.kind === 'probe' ? 'down' : x.issues[0]?.kind || x.tone} />)}
        </>
      ),
      onOpen: () => navigate(`/orgs/${org.id}/apps`),
    },
    ...(isAdmin ? [{
      id: 'costs', side: 'left', level: orgCost ? c.level : 'off', title: `Costs · ${period}`,
      body: orgCost ? (
        <>
          <strong>{usd(c.mtdUsd)}<span className="cmd-sub"> mtd</span></strong>
          <Row k="burn / day" v={usd(c.burnPerDayUsd)} />
          <Row k="projected" v={usd(c.projectedUsd)} />
          <Row k="limit" v={c.limitUsd ? `${usd(c.limitUsd)} · ${Math.round(c.ratio * 100)}%` : 'none'} />
          {c.platformUsd != null && <Row k="platform" v={usd(c.platformUsd)} />}
          {byApp.slice(0, 3).map(([id, a]) => <Row key={id} k={a.name} v={usd(a.mtdUsd)} />)}
        </>
      ) : <span className="cmd-sub">No cost data yet — the first rollup runs within the hour after any activity.</span>,
      onOpen: () => navigate(`/orgs/${org.id}/billing#costs`),
    }] : []),
    {
      id: 'usage', side: 'right', level: usage ? 'gold' : 'off', title: `Usage · ${period}`,
      body: usage ? (
        <>
          <strong>{n((usage.counters || {}).agentConversations || 0)}<span className="cmd-sub"> conversations</span></strong>
          <Row k="pipeline runs" v={n((usage.counters || {}).pipelineRuns || 0)} />
          <Row k="gated commits" v={n((usage.counters || {}).gatedCommits || 0)} />
          <Row k="HITL cycles" v={n((usage.counters || {}).hitlCycles || 0)} />
          {usage.updatedAt ? <span className="cmd-sub">updated {ago(usage.updatedAt)}</span> : null}
        </>
      ) : <span className="cmd-sub">No metered activity this month.</span>,
      onOpen: () => navigate(`/orgs/${org.id}/billing`),
    },
    {
      id: 'envs', side: 'right', level: 'ok', title: 'Environments',
      body: (
        <>
          <strong>{(apps || []).filter((a) => a.uatAppId).length}<span className="cmd-sub"> with UAT twin</span></strong>
          <Row k="customer-hosted" v={s.external.length} />
          <Row k="kit drift" v={t.drift} />
        </>
      ),
    },
    {
      id: 'scans', side: 'left', level: scanHigh ? 'warn' : 'ok', title: 'Security',
      body: (
        <>
          <strong>{scanHigh}<span className="cmd-sub"> with high+</span></strong>
          <Row k="accepted baseline" v={scanAccepted} />
          <Row k="live apps" v={liveApps.length} />
        </>
      ),
      onOpen: () => navigate(orgLive(org.id, 'security')),
    },
    { ...trafficPanel(telemetry, { side: 'right' }), onOpen: () => navigate(orgLive(org.id, 'traffic')) },
    { ...anomalyPanel(telemetry, { side: 'left' }), onOpen: () => navigate(orgLive(org.id, 'anomalies')) },
  ]), [data, t, s, isAdmin, orgCost, c, period, byApp, usage, apps, org.id, navigate, telemetry, scanHigh, scanAccepted, liveApps.length]);
  const onOpenDetail = useCallback((id) => {
    if (ORG_ASPECT[id]) { navigate(orgLive(org.id, ORG_ASPECT[id])); return true; }
    const prefix = `${org.id}/`;
    if (id.startsWith(prefix)) {
      const appId = id.slice(prefix.length);
      if (appId && !appId.includes('/')) { navigate(`/orgs/${org.id}/apps/${appId}`); return true; }
    }
    return false;
  }, [navigate, org.id]);

  const drawer = useMemo(() => {
    const out = {
      traffic: { title: `Inter-app flows · ${telemetry.window.label}`, render: () => <FlowTable t={telemetry} /> },
      anomalies: { title: `Anomalies · ${telemetry.window.label}`, render: () => <AnomalyTable t={telemetry} /> },
    };
    for (const a of apps || []) {
      const id = `${org.id}/${a.id}`;
      const x = s.nodes.find((y) => y.id === id);
      const ac = orgCost?.byApp?.[a.id];
      out[id] = {
        title: a.name || a.slug,
        render: () => (
          <>
            <div className="cmd-drawer-stats">
              <Row k="status" v={a.status} />
              <Row k="plan" v={a.plan || '—'} />
              {x && <Row k="health" v={x.monitorLevel === 'off' ? '—' : x.monitorLevel} />}
              {ac && <Row k="cost mtd" v={usd(ac.mtdUsd)} />}
              {ac && <Row k="projected" v={usd(ac.projectedUsd)} />}
              {ac?.lastActivityAt && <Row k="last activity" v={ago(ac.lastActivityAt)} />}
              {telemetry.live[a.slug] && <Row k={`traffic now${telemetry.source === 'sim' ? ' (sim)' : ''}`} v={`${Math.round(telemetry.live[a.slug].req)}/min · ${telemetry.live[a.slug].s5xx} 5xx · p95 ${Math.round(telemetry.live[a.slug].p95)} ms`} />}
              <RuntimeRows t={telemetry} slug={a.slug} />
            </div>
            {telemetry.anomalies.some((an) => an.slug === a.slug && an.closedAt == null) && <AnomalyTable t={{ anomalies: telemetry.anomalies.filter((an) => an.slug === a.slug) }} />}
            <div className="cmd-rail-actions" style={{ margin: '12px 0' }}>
              <Link className="cs-btn cs-btn-sm" to={`/orgs/${org.id}/apps/${a.id}`}>Open command view</Link>
              {a.urls?.app && <a className="cs-btn cs-btn-sm cs-btn-outline" href={a.urls.app} target="_blank" rel="noreferrer">Open app</a>}
            </div>
          </>
        ),
      };
    }
    return out;
  }, [apps, org.id, s, orgCost, telemetry]);

  const alert = org.costAlertState;
  return (
    <CommandView
      data={data}
      derive={deriveFleet}
      scene="fleet"
      panels={panels}
      drawer={drawer}
      events={s.events}
      caption={data ? s.caption : 'loading…'}
      chrome={chrome}
      className="is-org"
      telemetry={telemetry}
      legend={s.legend}
      defaultInspector={inspectorFor(null)}
      inspectorFor={inspectorFor}
      onOpenDetail={onOpenDetail}
      roomHref={`/orgs/${org.id}/room`}
    >
      {children}
      <TelemetryControls controls={controls} compact />
      <ActivityStrip telemetry={telemetry} />
      {isAdmin && alert && alert.level !== 'ok' && <div className={`cmd-rail-banner ${alert.level === 'over' ? 'is-down' : ''}`}>{alert.message}</div>}
    </CommandView>
  );
}
